// @intent: Versioned persisted ProjectBody contract from TZ sections 1.3 and 6.2
// @flow: DB projects.body -> ProjectBodySchema.parse -> runtime/editor services
// @stale: When ProjectBodyV2 is introduced, add V2 to ProjectBodySchema and lazy migration

import { z } from 'zod';

const BaseSceneNodeV1Schema = z.strictObject({
  id: z.string(),
  name: z.string(),
  visible: z.boolean(),
  locked: z.boolean(),
  x: z.number(),
  y: z.number(),
});

export type SceneNodeV1 =
  | {
      id: string;
      name: string;
      type: 'scene';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      children: SceneNodeV1[];
    }
  | {
      id: string;
      name: string;
      type: 'group';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      expanded?: boolean | undefined;
      children: SceneNodeV1[];
    }
  | {
      id: string;
      name: string;
      type: 'rect';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      width: number;
      height: number;
      color: string;
      assetUrl?: string | undefined;
      alpha?: number | undefined;
      children?: undefined;
    }
  | {
      id: string;
      name: string;
      type: 'sprite';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      width: number;
      height: number;
      assetUrl: string;
      tint?: string | undefined;
      alpha?: number | undefined;
      children?: undefined;
    }
  | {
      id: string;
      name: string;
      type: 'text';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      text: string;
      fontSize: number;
      color: string;
      fontFamily?: string | undefined;
      children?: undefined;
    }
  | {
      id: string;
      name: string;
      type: 'button';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      width: number;
      height: number;
      label: string;
      color: string;
      assetUrl?: string | undefined;
      children?: undefined;
    };

export const SceneNodeV1Schema: z.ZodType<SceneNodeV1> = z.lazy(() =>
  z.discriminatedUnion('type', [
    BaseSceneNodeV1Schema.extend({
      type: z.literal('scene'),
      children: z.array(SceneNodeV1Schema).default([]),
    }),
    BaseSceneNodeV1Schema.extend({
      type: z.literal('group'),
      expanded: z.boolean().optional(),
      children: z.array(SceneNodeV1Schema).default([]),
    }),
    BaseSceneNodeV1Schema.extend({
      type: z.literal('rect'),
      width: z.number(),
      height: z.number(),
      color: z.string(),
      assetUrl: z.string().optional(),
      alpha: z.number().optional(),
    }),
    BaseSceneNodeV1Schema.extend({
      type: z.literal('sprite'),
      width: z.number(),
      height: z.number(),
      assetUrl: z.string(),
      tint: z.string().optional(),
      alpha: z.number().optional(),
    }),
    BaseSceneNodeV1Schema.extend({
      type: z.literal('text'),
      text: z.string(),
      fontSize: z.number(),
      color: z.string(),
      fontFamily: z.string().optional(),
    }),
    BaseSceneNodeV1Schema.extend({
      type: z.literal('button'),
      width: z.number(),
      height: z.number(),
      label: z.string(),
      color: z.string(),
      assetUrl: z.string().optional(),
    }),
  ]),
);

export type SceneRootNodeV1 = Extract<SceneNodeV1, { type: 'scene' }>;

export type SceneGraphV1 = {
  version: 'v1';
  root: SceneRootNodeV1;
};

export const SceneGraphV1Schema: z.ZodType<SceneGraphV1> = z.strictObject({
  version: z.literal('v1'),
  root: SceneNodeV1Schema.pipe(z.custom<SceneRootNodeV1>(
    (node): node is SceneRootNodeV1 =>
      typeof node === 'object' && node !== null && (node as SceneNodeV1).type === 'scene',
    {
    message: 'Scene graph root must be a scene node',
    },
  )),
});

export const SlotRefV1Schema = z.strictObject({
  layerId: z.string(),
  label: z.string(),
  kind: z.string(),
  assetUrl: z.string().optional(),
  text: z.string().optional(),
  color: z.string().optional(),
  url: z.string().optional(),
});
export type SlotRefV1 = z.infer<typeof SlotRefV1Schema>;

export const SlotMapV1Schema = z.strictObject({
  version: z.literal('v1'),
  slots: z.record(z.string(), SlotRefV1Schema),
});
export type SlotMapV1 = z.infer<typeof SlotMapV1Schema>;

export const ProjectManifestV1Schema = z.strictObject({
  name: z.string(),
  viewport: z.strictObject({
    width: z.number(),
    height: z.number(),
  }),
  template: z.string().optional(),
});
export type ProjectManifestV1 = z.infer<typeof ProjectManifestV1Schema>;

export const AssetRefV1Schema = z.strictObject({
  id: z.string(),
  url: z.string(),
  mimeType: z.string(),
});
export type AssetRefV1 = z.infer<typeof AssetRefV1Schema>;

export const ProjectBodyV1Schema = z.strictObject({
  version: z.literal('v1'),
  manifest: ProjectManifestV1Schema,
  scene_graph: SceneGraphV1Schema,
  slot_map: SlotMapV1Schema,
  code: z.string().nullable(),
  assets: z.array(AssetRefV1Schema),
  variables: z.record(z.string(), z.unknown()),
});
export type ProjectBodyV1 = z.infer<typeof ProjectBodyV1Schema>;

export const ProjectBodySchema = z.discriminatedUnion('version', [ProjectBodyV1Schema]);
export type ProjectBody = z.infer<typeof ProjectBodySchema>;

type WithoutChildren<T> = T extends unknown ? Omit<T, 'children'> : never;
type FlatLayerV1 = WithoutChildren<SceneNodeV1> & { parentId?: string | undefined };

function canHaveChildren(node: SceneNodeV1): node is Extract<SceneNodeV1, { type: 'scene' | 'group' }> {
  return node.type === 'scene' || node.type === 'group';
}

function slotKindForNode(node: SceneNodeV1): string | null {
  switch (node.type) {
    case 'sprite':
      return 'asset';
    case 'rect':
      return 'background';
    case 'button':
      return 'cta';
    case 'text':
      return 'text';
    default:
      return null;
  }
}

function collectDefaultSlots(node: SceneNodeV1, slots: Record<string, SlotRefV1>): void {
  const kind = slotKindForNode(node);
  if (kind !== null) {
    slots[node.id] = { layerId: node.id, label: node.name, kind };
  }
  if (canHaveChildren(node)) {
    for (const child of node.children) collectDefaultSlots(child, slots);
  }
}

export function defaultSlotMapForSceneGraph(sceneGraph: SceneGraphV1): SlotMapV1 {
  const slots: Record<string, SlotRefV1> = {};
  collectDefaultSlots(sceneGraph.root, slots);
  return { version: 'v1', slots };
}

function cloneLayerNode(layer: FlatLayerV1): SceneNodeV1 {
  const { parentId, ...node } = layer;
  void parentId;

  switch (node.type) {
    case 'scene':
      return { ...node, children: [] };
    case 'group':
      return { ...node, children: [] };
    case 'rect':
      return node;
    case 'sprite':
      return node;
    case 'text':
      return node;
    case 'button':
      return node;
  }
}

export function flatLayersToSceneGraph(layers: readonly FlatLayerV1[]): SceneGraphV1 {
  const byId = new Map<string, SceneNodeV1>();
  const roots: SceneNodeV1[] = [];

  for (const layer of layers) {
    if (byId.has(layer.id)) {
      throw new Error(`flatLayersToSceneGraph: duplicate layer id "${layer.id}"`);
    }

    byId.set(layer.id, cloneLayerNode(layer));
  }

  for (const layer of layers) {
    const node = byId.get(layer.id);
    if (!node) {
      continue;
    }

    if (!layer.parentId) {
      roots.push(node);
      continue;
    }

    const parent = byId.get(layer.parentId);
    if (!parent) {
      throw new Error(`flatLayersToSceneGraph: parent "${layer.parentId}" not found for layer "${layer.id}"`);
    }
    if (!canHaveChildren(parent)) {
      throw new Error(`flatLayersToSceneGraph: parent "${layer.parentId}" cannot contain children`);
    }

    parent.children = [...parent.children, node];
  }

  const [root] = roots;
  if (!root || roots.length !== 1) {
    throw new Error('flatLayersToSceneGraph: exactly one root layer without parentId is required');
  }

  if (root.type !== 'scene') {
    throw new Error('flatLayersToSceneGraph: root layer must have type "scene"');
  }

  return { version: 'v1', root };
}
